import axios from 'axios';
import http from '@/services/http';
import TestService from '@/services/TestService';
import { defineStore } from 'pinia';
import cacheService from '@/services/cacheService';
import { API_PATHS } from '@/config/api';

// Pinia-Store für Tests
export const useTestsStore = defineStore('tests', {
  state: () => ({
    tests: [],
    currentTest: null,
    questions: [],
    loading: false,
    error: null,
    testsQuestionCount: [],
    loadingQuestionCount: false,
    questionCountError: null,
    sortableOptions: {
      animation: 200,
      ghostClass: 'ghost',
      handle: '.draggable-handle'
    }
  }),
  
  getters: {
    getAllTests: (state) => state.tests,
    getCurrentTest: (state) => state.currentTest,
    getQuestions: (state) => state.questions,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getTestsQuestionCount: (state) => state.testsQuestionCount,
    isLoadingQuestionCount: (state) => state.loadingQuestionCount,
    getQuestionCountError: (state) => state.questionCountError,
    getSortableOptions: (state) => state.sortableOptions
  },
  
  actions: {
    setTests(tests) {
      this.tests = tests;
    },
    setCurrentTest(test) {
      this.currentTest = test;
    },
    setQuestions(questions) {
      this.questions = questions;
    },
    addTest(test) {
      this.tests.push(test);
    },
    updateTest(updatedTest) {
      const index = this.tests.findIndex(t => t.id === updatedTest.id);
      if (index !== -1) {
        this.tests[index] = updatedTest;
      }
    },
    removeTestLocally(testId) {
      this.tests = this.tests.filter(t => t.id !== testId);
    },
    
    addQuestion(question) {
      this.questions.push(question);
    },
    updateQuestion(updatedQuestion) {
      const index = this.questions.findIndex(q => q.id === updatedQuestion.id);
      if (index !== -1) {
        this.questions[index] = updatedQuestion;
      }
    },
    deleteQuestion(questionId) {
      this.questions = this.questions.filter(q => q.id !== questionId);
    },
    reorderQuestions({ questionId, newIndex }) {
      const currentIndex = this.questions.findIndex(q => q.id === questionId);
      if (currentIndex === -1) return;
      
      const [movedQuestion] = this.questions.splice(currentIndex, 1);
      this.questions.splice(newIndex, 0, movedQuestion);
      
      // Update order values
      this.questions.forEach((question, index) => {
        question.order = index + 1;
      });
    },
    setLoading(status) {
      this.loading = status;
    },
    setError(error) {
      this.error = error;
    },
    setTestsQuestionCount(data) {
      this.testsQuestionCount = data;
    },
    setLoadingQuestionCount(status) {
      this.loadingQuestionCount = status;
    },
    setQuestionCountError(error) {
      this.questionCountError = error;
    },
    
    // Asynchrone Aktionen
    async fetchTests() {
      this.setLoading(true);
      this.setError(null);
      
      // Cache-Key für Tests
      const cacheKey = cacheService.createKey('tests');
      
      try {
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log('Tests aus dem Cache geladen');
          this.setTests(cachedData);
          this.setLoading(false);
          return cachedData;
        }
        
        const response = await http.get('/api/tests');
        this.setTests(response.data);
        // Speichere die Daten im Cache
        cacheService.set(cacheKey, response.data);
        return response.data;
      } catch (error) {
        this.setError(error.message || 'Fehler beim Abrufen der Tests');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async fetchTest(id) {
      this.setLoading(true);
      this.setError(null);
      
      // Cache-Key für einen einzelnen Test
      const cacheKey = cacheService.createKey('tests', id);
      
      try {
        console.log(`Lade Test mit ID ${id}...`);
        
        if (!id) {
          throw new Error('Keine gültige Test-ID angegeben');
        }
        
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log(`Test ${id} aus dem Cache geladen`);
          this.setCurrentTest(cachedData);
          this.setLoading(false);
          return cachedData;
        }
        
        const response = await http.get(`/api/tests/${id}`);
        console.log(`Test ${id} erfolgreich geladen:`, response.data);
        
        if (!response.data) {
          throw new Error('API hat keine Testdaten zurückgegeben');
        }
        
        this.setCurrentTest(response.data);
        
        // Speichere die Daten im Cache
        cacheService.set(cacheKey, response.data);
        
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Laden des Tests ${id}:`, error);
        const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Abrufen des Tests';
        this.setError(errorMessage);
        throw error;
      } finally {
        this.setLoading(false);
      }
    },

    async fetchTestByHash(hash) {
      this.setLoading(true);
      this.setError(null);
      
      // Cache-Key für Test mit Hash
      const cacheKey = cacheService.createKey('tests', `hash-${hash}`);
      
      try {
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log(`Test mit Hash ${hash} aus dem Cache geladen`);
          this.setCurrentTest(cachedData);
          
          // Fragen für den Test festlegen
          if (cachedData.questions) {
            this.setQuestions(cachedData.questions);
          }
          
          this.setLoading(false);
          return cachedData;
        }
        
        const response = await http.get(`/api/test/${hash}`);
        this.setCurrentTest(response.data);
        
        // Fragen für den Test festlegen
        if (response.data.questions) {
          this.setQuestions(response.data.questions);
        }
        
        // Speichere die Daten im Cache
        cacheService.set(cacheKey, response.data);
        
        return response.data;
      } catch (error) {
        this.setError('Test nicht gefunden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async createTest(testData) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log('Erstelle neuen Test:', testData);
        const response = await http.post(API_PATHS.TEST_CREATE, testData);
        
        if (!response.data) {
          throw new Error('API hat keine Testdaten zurückgegeben');
        }
        
        this.addTest(response.data);
        return response.data;
      } catch (error) {
        console.error('Fehler beim Erstellen des Tests:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Erstellen des Tests';
        this.setError(errorMessage);
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async updateTestData(id, testData) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        console.log(`Aktualisiere Test ${id}:`, testData);
        const response = await http.put(API_PATHS.TEST_UPDATE(id), testData);
        
        if (!response.data) {
          throw new Error('API hat keine Testdaten zurückgegeben');
        }
        
        this.updateTest(response.data);
        return response.data;
      } catch (error) {
        console.error(`Fehler beim Aktualisieren des Tests ${id}:`, error);
        const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Aktualisieren des Tests';
        this.setError(errorMessage);
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    async fetchQuestions(testId) {
      this.setLoading(true);
      this.setError(null);
      
      // Cache-Key für Fragen eines Tests
      const cacheKey = cacheService.createKey('tests', testId, 'questions');
      
      try {
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log(`Fragen für Test ${testId} aus dem Cache geladen`);
          this.setQuestions(cachedData);
          this.setLoading(false);
          return cachedData;
        }
        
        console.log(`Lade Fragen für Test ${testId}...`);
        const response = await http.get(`/api/tests/${testId}/questions`);
        
        // Überprüfen, ob die Antwort ein Array enthält
        if (Array.isArray(response.data)) {
          console.log(`${response.data.length} Fragen für Test ${testId} geladen`);
          this.setQuestions(response.data);
          
          // Speichere die Daten im Cache
          cacheService.set(cacheKey, response.data);
          
          return response.data;
        } else {
          console.error('API hat keine Fragen-Array zurückgegeben:', response.data);
          this.setQuestions([]);
          this.setError('Format der Fragen ist ungültig');
          return [];
        }
      } catch (error) {
        console.error('Fehler beim Laden der Fragen:', error);
        this.setError('Fehler beim Laden der Fragen');
        this.setQuestions([]);
        throw error;
      } finally {
        this.setLoading(false);
      }
    },
    
    // Hilfsfunktion für die Konvertierung von Frontend zu Backend-Format
    _prepareQuestionForBackend(question) {
      // Ein frisches Objekt erstellen, um nur die benötigten Werte zu übertragen
      const backendData = {
        type: question.type || 'multiple_choice',
        description: question.description || '',
        points: question.points || 10,
        allow_multiple_answers: question.allow_multiple_answers || false
      };
      
      // sort_order nur senden, wenn es explizit gesetzt ist (mindestens 1)
      if (question.sort_order && question.sort_order >= 1) {
        backendData.sort_order = question.sort_order;
      }
      
      // question_group_id nur senden, wenn es gesetzt ist
      if (question.question_group_id) {
        backendData.question_group_id = question.question_group_id;
      }
      
      // max_points nur senden, wenn es gesetzt ist und größer als 0
      if (question.max_points && question.max_points > 0) {
        backendData.max_points = question.max_points;
      }
      
      // Direkte Übergabe der Felder in Backend-Format
      if (question.answer_1) backendData.answer_1 = question.answer_1;
      if (question.answer_2) backendData.answer_2 = question.answer_2;
      if (question.answer_3) backendData.answer_3 = question.answer_3;
      if (question.answer_4) backendData.answer_4 = question.answer_4;
      if (question.answer_5) backendData.answer_5 = question.answer_5;
      if (question.answer_6) backendData.answer_6 = question.answer_6;
      
      if (question.is_answer_1_correct !== undefined) backendData.is_answer_1_correct = question.is_answer_1_correct;
      if (question.is_answer_2_correct !== undefined) backendData.is_answer_2_correct = question.is_answer_2_correct;
      if (question.is_answer_3_correct !== undefined) backendData.is_answer_3_correct = question.is_answer_3_correct;
      if (question.is_answer_4_correct !== undefined) backendData.is_answer_4_correct = question.is_answer_4_correct;
      if (question.is_answer_5_correct !== undefined) backendData.is_answer_5_correct = question.is_answer_5_correct;
      if (question.is_answer_6_correct !== undefined) backendData.is_answer_6_correct = question.is_answer_6_correct;
      
      return backendData;
    },
    
    async updateQuestion(testId, questionId, question = {}) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        if (!testId || !questionId) {
          console.error('Test-ID oder Frage-ID fehlt:', { testId, questionId });
          throw new Error('Test-ID und Frage-ID sind erforderlich');
        }
        
        // Daten für das Backend aufbereiten
        const backendData = this._prepareQuestionForBackend(question);
        
        console.log(`Aktualisiere Frage ${questionId} für Test ${testId}:`, JSON.stringify(backendData, null, 2));
        console.log(`PUT ${http.defaults.baseURL}/api/tests/${testId}/questions/${questionId}`);
        
      const response = await http.put(`/api/tests/${testId}/questions/${questionId}`, backendData);
        console.log(`Antwort vom Server für Frage ${questionId}:`, response.data);
        
        // Ersetze einen direkten Aufruf der gleichnamigen Methode mit dem Methodenaufruf aus dem State
        // this.updateQuestion(response.data) -> Führt zu rekursivem Aufruf
        const index = this.questions.findIndex(q => q.id === response.data.id);
        if (index !== -1) {
            this.questions[index] = response.data;
            console.log(`Frage an Index ${index} im Store aktualisiert`);
        } else {
            console.warn(`Frage mit ID ${response.data.id} nicht im Store gefunden`);
        }
        
        // Cache für Fragen dieses Tests invalidieren
        cacheService.invalidateEntityType('tests');
        console.log('Cache für Tests invalidiert');
        
      return response.data;
    } catch (error) {
        console.error('API-Fehler beim Aktualisieren der Frage:', error);
        console.error('Fehlerdetails:', error.response?.data);
        console.error('Anfragestatus:', error.response?.status);
        this.setError('Fehler beim Aktualisieren der Frage');
      throw error;
    } finally {
        this.setLoading(false);
      }
    },
    
    async createQuestion(testId, question = {}) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        if (!testId) {
          throw new Error('Test-ID ist erforderlich');
        }
        
        // Daten für das Backend aufbereiten
        const backendData = this._prepareQuestionForBackend(question);
        
        console.log('Sending question data to API:', JSON.stringify(backendData, null, 2));
        const response = await http.post(`/api/tests/${testId}/questions`, backendData);
        this.addQuestion(response.data);
        
        // Cache für Fragen dieses Tests invalidieren
        cacheService.invalidateEntityType('tests');
        
        return response.data;
    } catch (error) {
        console.error('API error when creating question:', error.response?.data);
        this.setError('Fehler beim Hinzufügen der Frage');
      throw error;
    } finally {
        this.setLoading(false);
      }
    },
    
    async updateQuestionsOrder(testId, questionsOrder) {
      this.setLoading(true);
      this.setError(null);
    
    try {
      const response = await http.post(`/api/tests/${testId}/questions/reorder`, { questions: questionsOrder });
        this.setQuestions(response.data);
        
        // Cache für Fragen dieses Tests invalidieren
        cacheService.invalidateEntityType('tests');
        
      return response.data;
    } catch (error) {
        this.setError('Fehler beim Aktualisieren der Reihenfolge');
      throw error;
    } finally {
        this.setLoading(false);
      }
    },
    
    async fetchTestsQuestionCount() {
      this.setLoadingQuestionCount(true);
      this.setQuestionCountError(null);
      
      // Cache-Key für die Anzahl der Fragen
      const cacheKey = cacheService.createKey('tests', 'question-count');
      
      try {
        // Prüfen, ob Daten im Cache sind
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          console.log(`Fragenanzahl aus dem Cache geladen`);
          this.setTestsQuestionCount(cachedData);
          this.setLoadingQuestionCount(false);
          return cachedData;
        }
        
        const response = await http.get(API_PATHS.TESTS_QUESTION_COUNT);
        this.setTestsQuestionCount(response.data);
        
        // Speichere die Daten im Cache
        cacheService.set(cacheKey, response.data);
        
        return response.data;
      } catch (error) {
        console.error('Fehler beim Abrufen der Fragenanzahl:', error);
        this.setQuestionCountError(error.message);
        throw error;
      } finally {
        this.setLoadingQuestionCount(false);
      }
    },
    
    // Manuelle Invalidierung des Caches
    invalidateCache() {
      cacheService.invalidateEntityType('tests');
      console.log('Tests-Cache wurde manuell invalidiert');
    },
    
    async deleteQuestion(questionId) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        if (!questionId) {
          throw new Error('Frage-ID ist erforderlich');
        }
        
        console.log(`Lösche Frage ${questionId}...`);
        await http.delete(`/api/questions/${questionId}`);
        
        // Cache für Fragen invalidieren
        cacheService.invalidateEntityType('tests');
        
        // Entferne die Frage aus dem lokalen Zustand, falls sie vorhanden ist
        const index = this.questions.findIndex(q => q.id === questionId);
        if (index !== -1) {
          this.questions.splice(index, 1);
        }
        
        return true;
      } catch (error) {
        console.error(`Fehler beim Löschen der Frage ${questionId}:`, error);
        this.setError('Frage konnte nicht gelöscht werden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },

    async deleteTest(testId) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        if (!testId) {
          throw new Error('Test-ID ist erforderlich');
        }
        
        console.log(`Lösche Test ${testId}...`);
        await http.delete(API_PATHS.TEST_DELETE(testId));
        
        // Cache für Tests invalidieren
        cacheService.invalidateEntityType('tests');
        
        // Entferne den Test aus dem lokalen Zustand
        this.removeTestLocally(testId);
        
        return true;
      } catch (error) {
        console.error(`Fehler beim Löschen des Tests ${testId}:`, error);
        this.setError('Test konnte nicht gelöscht werden');
        throw error;
      } finally {
        this.setLoading(false);
      }
    },

    async reorderTests(testsOrder) {
      this.setLoading(true);
      this.setError(null);
      
      try {
        const response = await http.post(API_PATHS.TESTS_REORDER, testsOrder);
        this.setTests(response.data);
        
        // Cache für Tests invalidieren
        cacheService.invalidateEntityType('tests');
        
        return response.data;
      } catch (error) {
        this.setError('Fehler beim Aktualisieren der Reihenfolge der Tests');
        throw error;
      } finally {
        this.setLoading(false);
      }
    }
  }
}); 